/**
 * 계정 생애주기 API 클라이언트 — 초대·자가가입·사용자 설정(D-02·D-14).
 *
 * - 공개 흐름(signup/verifySignup/getInvitation/acceptInvitation)은 미인증 호출이므로 fetch 직접 사용.
 * - 인증 흐름(joinInvitation/createInvitation/getSettings/updateSettings)은 apiFetch(토큰 자동첨부) 사용.
 */

import { apiFetch } from "@/lib/api-client";
import type { SupportedLng } from "@/lib/i18n";

const API_BASE = import.meta.env.VITE_API_URL ?? "";

/** 초대 수락 결과(=사용자·워크스페이스 식별자). 기존 SignupResponse 계약 재사용. */
export interface SignupResponse {
  userId: string;
  companyId: string;
  workspaceId: string;
  workspaceSlug: string;
  email: string;
  name: string;
}

export interface InvitationResponse {
  id: string;
  email: string;
  workspaceId: string;
  workspaceName: string;
  companyName: string;
  expiresAt: string | null;
  accepted: boolean;
  /** 이미 가입된 사용자면 true — 신규 가입(비번 설정) 대신 합류(joinInvitation) 경로. */
  existingUser: boolean;
}

export interface AccountSettings {
  userId: string;
  email: string;
  name: string;
  locale: string;
}

/** 자가가입(초대 없음) 요청 — 계정 + 회사 + 워크스페이스 + 프리셋. 비밀번호는 인증 단계에서 설정. */
export interface SignupCreateRequest {
  email: string;
  name: string;
  companyName: string;
  plan: string;
  preset: string;
  workspaceName: string;
  /** 가입 화면의 현재 UI 언어 — 서버가 인증 메일을 이 언어로 보냄(지원 언어가 아니면 서버가 무시하고 ko). */
  locale?: SupportedLng;
}

/**
 * 자가가입 요청(미인증) — 회사·유저는 만들지 않고 pending 보관 + 인증 메일 발송(202). 실제 생성은 인증 링크
 * 클릭({@link verifySignup})에서 비밀번호 설정 시 이뤄짐.
 */
export async function signup(req: SignupCreateRequest): Promise<void> {
  const res = await fetch(`${API_BASE}/api/account/signup`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string; title?: string };
    throw new Error(body.detail ?? body.title ?? `signup failed: ${res.status}`);
  }
}

/** 자가가입 이메일 인증(생성, 미인증) — 토큰 + 비밀번호로 회사·유저·워크스페이스·모듈·CompanyAdmin 생성. */
export async function verifySignup(req: {
  token: string;
  password: string;
}): Promise<SignupResponse> {
  const res = await fetch(`${API_BASE}/api/account/signup/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string; title?: string };
    throw new Error(body.detail ?? body.title ?? `verify failed: ${res.status}`);
  }
  return (await res.json()) as SignupResponse;
}

/** 초대 정보 조회(미인증, 수락 페이지 사전표시). */
export async function getInvitation(token: string): Promise<InvitationResponse> {
  const res = await fetch(`${API_BASE}/api/account/invitations/${encodeURIComponent(token)}`, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { title?: string };
    throw new Error(body.title ?? `invitation lookup failed: ${res.status}`);
  }
  return (await res.json()) as InvitationResponse;
}

/** 초대 수락(신규 유저, 미인증) — 사용자 생성 + 워크스페이스 합류. */
export async function acceptInvitation(req: {
  token: string;
  name: string;
  password: string;
}): Promise<SignupResponse> {
  const res = await fetch(`${API_BASE}/api/account/invitations/accept`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { title?: string; detail?: string };
    throw new Error(body.detail ?? body.title ?? `accept failed: ${res.status}`);
  }
  return (await res.json()) as SignupResponse;
}

/**
 * 초대 합류(기존 유저) — 신규 가입·비번 없이 워크스페이스에 일반 멤버로 합류. <b>인증 필요</b>: 초대받은 계정으로 로그인한 상태에서 호출한다(서버가 로그인
 * 사용자=초대 대상 검증). apiFetch 가 Authorization 토큰을 첨부함.
 */
export function joinInvitation(token: string): Promise<SignupResponse> {
  return apiFetch<SignupResponse>("/api/account/invitations/join", {
    method: "POST",
    body: { token },
  });
}

/**
 * 팀원 초대 생성(인증 필요). 초대는 현재 워크스페이스로 합류시키므로
 * X-Workspace-Slug 헤더(workspaceSlug)가 필요하다(BE 가 워크스페이스 컨텍스트 요구).
 */
export function createInvitation(
  req: { email: string; roleId?: string },
  workspaceSlug?: string,
): Promise<InvitationResponse> {
  return apiFetch<InvitationResponse>("/api/account/invitations", {
    method: "POST",
    body: req,
    workspaceSlug,
  });
}

/** 현재 사용자 설정(언어·프로필) 조회(인증 필요). */
export function getSettings(): Promise<AccountSettings> {
  return apiFetch<AccountSettings>("/api/account/settings");
}

/** 현재 사용자 설정(언어) 갱신(인증 필요). */
export function updateSettings(req: { locale: string }): Promise<AccountSettings> {
  return apiFetch<AccountSettings>("/api/account/settings", {
    method: "PUT",
    body: req,
  });
}
